import React from 'react';
import PropTypes from 'prop-types';
import Price from '../../Price';
import '../../shared/Card.css';
import './ImageSelector.css';

/**
 * ImageSelector — Configurator
 *
 * Selectable image tile (e.g. a letter or motif option): square white image
 * well, title and a label-size price underneath.
 *
 * Figma component set: 4290:24169
 * Variants: State = Default (4290:24170) | Hover (4290:24180) | Selected (4290:24190)
 *           Details / Title / Price — boolean toggles
 *
 * The Figma booleans map to data: the details block renders when `title` or
 * `price` is set. Hover is CSS-only; Selected comes from the `selected` prop.
 */
const ImageSelector = ({
  image,
  imageAlt = '',
  title,
  price,
  selected = false,
  imageFit = 'contain',
  onClick,
  className = '',
  ...props
}) => {
  const hasDetails = Boolean(title || price);

  const classes = [
    'ds-card',
    'ds-card--interactive',
    'cfg-image-selector',
    selected && 'ds-card--selected',
    imageFit === 'cover' && 'cfg-image-selector--cover',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-pressed={selected}
      {...props}
    >
      <span className="cfg-image-selector__image">
        {image && <img className="cfg-image-selector__img" src={image} alt={imageAlt} />}
      </span>

      {hasDetails && (
        <span className="cfg-image-selector__details">
          {title && <span className="cfg-image-selector__title typo-body-sm">{title}</span>}
          {price && <Price type="small" price={price} className="cfg-image-selector__price" />}
        </span>
      )}
    </button>
  );
};

ImageSelector.propTypes = {
  /** Option image URL (contained with a 12.5% inset on white) */
  image: PropTypes.string,
  /** Alt text for the image */
  imageAlt: PropTypes.string,
  /** Option name (typo-body-sm) */
  title: PropTypes.string,
  /** Formatted price, e.g. "49 kr" — DS Price at label size */
  price: PropTypes.string,
  /** Selected state — gray-10 border */
  selected: PropTypes.bool,
  /** 'contain' (default): cut-out artwork with the 12.5% inset on white; 'cover': a photo filling the well */
  imageFit: PropTypes.oneOf(['contain', 'cover']),
  /** Click handler */
  onClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default ImageSelector;
